/**
 * API конструктора шаблонов карточек ГО.
 */

import { del, get, post, put } from "@/lib/api-client-v2";
import type {
  GoCardTemplateListResponse,
  GoCardTemplateWithDetails,
  GoCardFieldType,
} from "@/types/go-card-template";
import type {
  CardFieldWidgetTypeItem,
  CardTemplateImportResponse,
  CardTemplatePublishRequest,
  CardTemplatePublishResponse,
  CardTemplateRestoreRequest,
} from "@/types/card-template";

export interface GoCardTemplateCreatePayload {
  code: string;
  name: string;
  description?: string | null;
  entity_type_kls_id?: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
}

export interface GoCardTemplateUpdatePayload {
  name?: string;
  description?: string | null;
  entity_type_kls_id?: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
  is_active?: boolean;
}

export interface GoCardSectionPayload {
  code: string;
  name: string;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_open?: boolean;
  ui_config?: Record<string, unknown>;
}

export interface GoCardSectionUpdatePayload {
  code?: string;
  name?: string;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_open?: boolean;
  ui_config?: Record<string, unknown>;
}

export interface GoCardFieldPayload {
  field_key: string;
  label: string;
  description?: string | null;
  section_id?: number | null;
  field_type: GoCardFieldType;
  qual_id: number;
  field_type_kls_id?: number | null;
  kls_id?: number | null;
  is_required?: boolean;
  is_readonly?: boolean;
  display_order?: number;
  is_active?: boolean;
}

export interface GoCardFieldUpdatePayload {
  field_key?: string;
  label?: string;
  description?: string | null;
  section_id?: number | null;
  field_type?: GoCardFieldType;
  qual_id?: number;
  field_type_kls_id?: number | null;
  kls_id?: number | null;
  is_required?: boolean;
  is_readonly?: boolean;
  display_order?: number;
  is_active?: boolean;
}

export const goCardTemplatesApi = {
  listFieldWidgetTypes: () =>
    get<CardFieldWidgetTypeItem[]>("/go-card-templates/meta/field-widget-types"),

  list: (params?: {
    status?: string;
    entity_type_kls_id?: number;
    global_type_only?: boolean;
    is_active?: boolean;
    skip?: number;
    limit?: number;
  }) => get<GoCardTemplateListResponse>("/go-card-templates", params ?? {}),

  get: (id: number) => get<GoCardTemplateWithDetails>(`/go-card-templates/${id}`),

  create: (body: GoCardTemplateCreatePayload) =>
    post<GoCardTemplateWithDetails>("/go-card-templates", body),

  update: (id: number, body: GoCardTemplateUpdatePayload) =>
    put<GoCardTemplateWithDetails>(`/go-card-templates/${id}`, body),

  publish: (id: number, body?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(`/go-card-templates/${id}/publish`, body ?? {}),

  archive: (id: number, body?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(`/go-card-templates/${id}/archive`, body ?? {}),

  restore: (id: number, body?: CardTemplateRestoreRequest) =>
    post<CardTemplatePublishResponse>(`/go-card-templates/${id}/restore`, body ?? {}),

  delete: (id: number) => del<void>(`/go-card-templates/${id}`),

  clone: (templateId: number, newCode: string) =>
    post<GoCardTemplateWithDetails>(
      `/go-card-templates/${templateId}/clone?new_code=${encodeURIComponent(newCode)}`,
      {},
    ),

  /** JSON-пакет schema geopulseml.go_card_template_export v1 */
  exportTemplateJson: (templateId: number) =>
    get<Record<string, unknown>>(`/go-card-templates/${templateId}/export`),

  exportTemplatesBulk: (templateIds: number[]) =>
    post<Record<string, unknown>>("/go-card-templates/export", { template_ids: templateIds }),

  importTemplatesJson: (
    bundle: Record<string, unknown>,
    onConflict: "error" | "skip" | "suffix" = "error",
  ) => {
    const q = new URLSearchParams({ on_conflict: onConflict });
    return post<CardTemplateImportResponse>(`/go-card-templates/import?${q.toString()}`, bundle);
  },

  addSection: (templateId: number, body: GoCardSectionPayload) =>
    post<GoCardTemplateWithDetails>(`/go-card-templates/${templateId}/sections`, body),

  updateSection: (templateId: number, sectionId: number, body: GoCardSectionUpdatePayload) =>
    put<GoCardTemplateWithDetails>(`/go-card-templates/${templateId}/sections/${sectionId}`, body),

  deleteSection: (templateId: number, sectionId: number) =>
    del<GoCardTemplateWithDetails>(`/go-card-templates/${templateId}/sections/${sectionId}`),

  addField: (templateId: number, body: GoCardFieldPayload) =>
    post<GoCardTemplateWithDetails>(`/go-card-templates/${templateId}/fields`, body),

  updateField: (templateId: number, fieldId: number, body: GoCardFieldUpdatePayload) =>
    put<GoCardTemplateWithDetails>(`/go-card-templates/${templateId}/fields/${fieldId}`, body),

  deleteField: (templateId: number, fieldId: number) =>
    del<GoCardTemplateWithDetails>(`/go-card-templates/${templateId}/fields/${fieldId}`),
};
